import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import NavRow from '../fields/NavRow';
import RadioField from '../fields/RadioField';
import TextAreaField from '../fields/TextAreaField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { DisciplinaryValues } from '../types';
import { zodResolver } from '../validation';

const schema = z
  .object({
    disciplinaryAction: z.enum(['Yes', 'No'], {
      required_error: 'Please choose Yes or No',
      invalid_type_error: 'Please choose Yes or No',
    }),
    details: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.disciplinaryAction === 'Yes' && !values.details?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['details'],
        message: 'Please explain below',
      });
    }
  });

const DEFAULTS: Partial<DisciplinaryValues> = {};

const DisciplinaryForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as DisciplinaryValues | undefined) ?? (DEFAULTS as DisciplinaryValues);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<DisciplinaryValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const answer = useWatch({ control, name: 'disciplinaryAction' });

  const onSubmit = handleSubmit((values) => {
    const cleaned: DisciplinaryValues = {
      disciplinaryAction: values.disciplinaryAction,
      ...(values.disciplinaryAction === 'Yes' && values.details ? { details: values.details } : {}),
    };
    onComplete(cleaned);
  });

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()}>
      <RadioField
        control={control}
        name="disciplinaryAction"
        options={[
          { title: 'Yes', value: 'Yes' },
          { title: 'No', value: 'No' },
        ]}
        required
      />
      {answer === 'Yes' && (
        <TextAreaField
          control={control}
          name="details"
          label="Please explain below: Include dates, incident details, and resolutions."
          required
        />
      )}
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Disciplinary: TaskDefinition = {
  key: 'task11',
  label: 'Disciplinary',
  title: 'Has any disciplinary action been taken against you or your firm by any state bar?',
  render: (args) => <DisciplinaryForm {...args} />,
};

export default Disciplinary;
